import { PartnershipTier } from '@/collections/App/constants'
import {
  Recommendation,
  RECOMMENDATION_SORT_ORDER,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { selectPrimaryRecommendation } from '@/lib/app-categories/discontinued'
import { isPopulatedObject } from '@/lib/utils'
import type { App, AppCategoryRecommendation, Comparison, ComparisonAppsApp } from '@/payload-types'

type RankedApp = Pick<App, 'id' | 'partnershipTier' | 'categoryRecommendations'>

const tierOrder: Record<PartnershipTier, number> = {
  [PartnershipTier.Platinum]: 0,
  [PartnershipTier.Gold]: 1,
  [PartnershipTier.Silver]: 2,
  [PartnershipTier.Bronze]: 3,
  [PartnershipTier.Sponsored]: 4,
}

function recommendationRank(app: RankedApp): number {
  const primary = selectPrimaryRecommendation(
    (app.categoryRecommendations?.docs ?? []).filter(isPopulatedObject<AppCategoryRecommendation>),
  )
  return RECOMMENDATION_SORT_ORDER[primary?.recommendation ?? Recommendation.NotRecommended]
}

function compareApps(a: RankedApp, b: RankedApp): number {
  return (
    (a.partnershipTier ? tierOrder[a.partnershipTier] : 5) -
      (b.partnershipTier ? tierOrder[b.partnershipTier] : 5) ||
    recommendationRank(a) - recommendationRank(b)
  )
}

export function sortBlogApps<T extends RankedApp>(apps: T[]): T[] {
  return [...apps].sort(compareApps)
}

export function sortDealsByAppOrder<T extends { appId: number }>(
  deals: T[],
  apps: RankedApp[],
): T[] {
  const positions = new Map(apps.map((app, index) => [app.id, index]))
  return [...deals].sort(
    (a, b) => (positions.get(a.appId) ?? apps.length) - (positions.get(b.appId) ?? apps.length),
  )
}

export function sortBlogComparisons<T extends Pick<Comparison, 'comparisonApps'>>(
  comparisons: T[],
  apps: RankedApp[],
): T[] {
  const byId = new Map(apps.map((app) => [app.id, app]))
  const ranked = comparisons.map((comparison) => ({
    comparison,
    apps: sortBlogApps(
      (comparison.comparisonApps?.docs ?? []).flatMap((relation) => {
        if (!isPopulatedObject<ComparisonAppsApp>(relation)) return []
        const id = isPopulatedObject<App>(relation.app) ? relation.app.id : relation.app
        const app = byId.get(id) ?? (isPopulatedObject<App>(relation.app) ? relation.app : undefined)
        return app ? [app] : []
      }),
    ),
  }))

  return ranked
    .sort((a, b) => {
      for (let index = 0; index < Math.max(a.apps.length, b.apps.length); index++) {
        if (!a.apps[index]) return 1
        if (!b.apps[index]) return -1
        const difference = compareApps(a.apps[index], b.apps[index])
        if (difference) return difference
      }
      return 0
    })
    .map(({ comparison }) => comparison)
}
